/**
 * The verification engine's register, its lines and its transitions.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The conduct screen is the checklist-as-report-card. Line outcomes are recorded one
 * line at a time and the server locks each line behind its answer; the results banner
 * names the decision rule that judged the submission, read from the server's own
 * evaluation, never reconstructed on the client.
 */

import { apiJson, bool, listBody, num, optStr, query, str } from "./client";
import { approvalRoute, type ApprovalRoute } from "./conduct";

export interface VerificationSummary {
  id: string;
  humanId: string;
  title: string;
  inspectionType: string;
  location: string | null;
  plannedDate: string;
  workflowState: string;
  outcome: string | null;
  recordStatus: string;
  contractId: string | null;
  contractHumanId: string | null;
  contractTitle: string;
  contractorPartyName: string;
  itpTitle: string;
  verificationTypeLabel: string;
  lineCount: number;
  /** The inspection day: which of the three sources fed it (RFI, OPERATOR_REQUEST, PLANNED_ACTIVITY). */
  origin: string;
  disciplineLabel: string | null;
  resultsApprovedAt: string | null;
  closedAt: string | null;
}

export interface Line {
  id: string;
  lineNo: number;
  itemDescription: string;
  inspectionMethod: string;
  acceptanceCriteria: string;
  referenceDocument: string | null;
  outcomeValue: string | null;
  comments: string | null;
  isPositiveObservation: boolean;
  lockedAt: string | null;
  /** The checklist numbering this line was loaded with ("2.1.3"); null for an ITP line. */
  lineNumber: string | null;
  sectionNumber: string | null;
  sectionTitle: string | null;
}

export interface TransitionEntry {
  id: string;
  event: string;
  fromState: string;
  toState: string;
  comment: string | null;
  at: string;
  actorDisplayName: string | null;
}

export interface VerificationDetail extends VerificationSummary {
  inspectedComponent: string | null;
  procedureDrawing: string | null;
  plannedTime: string | null;
  declarationConfirmed: boolean;
  warningAdvisories: string[];
  lines: Line[];
  assessors: { personDisplayName: string; isLead: boolean; roleLabel: string }[];
  transitions: TransitionEntry[];
  /** Present only once the server has judged the results; absent means not yet judged. */
  resultBanner: { evaluatedUnder: { rule: string; version: string } | null } | null;
  /**
   * The conduct and approval acts THIS caller may take now (START, SAVE, SUBMIT, APPROVE,
   * RETURN), each answered by its verb's predicate on the server.
   */
  conductActs: string[];
  scheduledActivityId: string | null;
  resultsApprovedAt: string | null;
  /** Where the results go (or wait) for approval, in role words; null when none. */
  approvalRoute: ApprovalRoute | null;
  /**
   * Wave R0 Break 9: the record closed while carrying a rejected line (its findings all
   * settled). The item it inspected went back to ACCEPTED and may be requested again.
   */
  closedWithRejections: boolean;
}

function assertRecord(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected an object body");
  }
  return body as Record<string, unknown>;
}

function summary(body: Record<string, unknown>): VerificationSummary {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    title: str(body.title),
    inspectionType: str(body.inspection_type),
    location: optStr(body.location),
    plannedDate: str(body.planned_date),
    workflowState: str(body.workflow_state),
    outcome: optStr(body.outcome),
    recordStatus: str(body.record_status),
    contractId: optStr(body.contract_id),
    contractHumanId: optStr(body.contract_human_id),
    contractTitle: str(body.contract_title),
    contractorPartyName: str(body.contractor_party_name),
    itpTitle: optStr(body.itp_title) ?? "",
    verificationTypeLabel: str(body.verification_type_label),
    lineCount: num(body.line_count),
    origin: optStr(body.origin) ?? "RFI",
    disciplineLabel: optStr(body.discipline_label),
    resultsApprovedAt: optStr(body.results_approved_at),
    closedAt: optStr(body.closed_at),
  };
}

export async function listVerifications(
  token: string,
  filters: {
    contractId?: string | null;
    itpId?: string | null;
    workflowState?: string | null;
    /** The stage/discipline stamp filters (P2): the rows carry both axes. */
    stageId?: string | null;
    disciplineId?: string | null;
    /** Narrows to verifications this principal is assigned to assess (ADR-025). */
    mine?: boolean;
    /** Wave W1: the results queue, only results at final approval the caller may approve. */
    pendingForMe?: boolean;
    signal?: AbortSignal;
  } = {},
): Promise<{ total: number; items: VerificationSummary[] }> {
  const body = await apiJson(
    token,
    `/verifications${query({
      contract_id: filters.contractId,
      itp_id: filters.itpId,
      workflow_state: filters.workflowState,
      stage_id: filters.stageId,
      discipline_id: filters.disciplineId,
      mine: filters.mine,
      pending_for_me: filters.pendingForMe === true ? true : undefined,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(summary) };
}

function line(body: Record<string, unknown>): Line {
  return {
    id: str(body.id),
    lineNo: num(body.line_no),
    itemDescription: str(body.item_description),
    inspectionMethod: str(body.inspection_method),
    acceptanceCriteria: str(body.acceptance_criteria),
    referenceDocument: optStr(body.reference_document),
    outcomeValue: optStr(body.outcome_value),
    comments: optStr(body.comments),
    isPositiveObservation: bool(body.is_positive_observation),
    lockedAt: optStr(body.locked_at),
    lineNumber: optStr(body.line_number),
    sectionNumber: optStr(body.section_number),
    sectionTitle: optStr(body.section_title),
  };
}

function transition(body: Record<string, unknown>): TransitionEntry {
  return {
    id: str(body.id),
    event: str(body.event),
    fromState: str(body.from_state),
    toState: str(body.to_state),
    comment: optStr(body.comment),
    at: str(body.at),
    actorDisplayName: optStr(body.actor_display_name),
  };
}

/**
 * The detail, with the results banner read out of the server's answer.
 *
 * The wire shape today carries the outcome and the transition history. The decision
 * rule's name and version arrive with the configuration track's evaluation record
 * (Invariant 7); until then `resultBanner.evaluatedUnder` is null, which renders as no
 * banner claim rather than an invented one. The field is read if present and never
 * defaulted to a rule the server did not name.
 */
export async function getVerification(
  token: string,
  verificationId: string,
  signal?: AbortSignal,
): Promise<VerificationDetail> {
  return apiJson(
    token,
    `/verifications/${verificationId}`,
    (raw) => {
      const body = assertRecord(raw);
      const assessors = Array.isArray(body.assessors) ? body.assessors : [];
      const transitions = Array.isArray(body.transitions) ? body.transitions : [];
      // The detail wire shape (schema.d.ts VerificationDetail) carries no summary-only
      // labels (contract/ITP titles, party name, type label, line count) and names the
      // judged rule through the `decision` evaluation object, not flat fields. Absent
      // labels render as absence; the banner names only what the server sent.
      const decision =
        typeof body.decision === "object" && body.decision !== null
          ? (body.decision as Record<string, unknown>)
          : null;
      const evaluation =
        decision !== null &&
        typeof decision.rule_key === "string" &&
        decision.rule_key !== "" &&
        typeof decision.decision_rule_version === "number"
          ? {
              rule: decision.rule_key,
              version: String(decision.decision_rule_version),
            }
          : null;
      return {
        // The detail wire shape omits the list-only labels (contract/ITP titles, party
        // name, type label, line count); they are read tolerantly and render absence.
        ...summary({
          ...body,
          contract_title: "",
          contractor_party_name: "",
          itp_title: "",
          verification_type_label: "",
          line_count: 0,
        }),
        inspectedComponent: optStr(body.inspected_component),
        procedureDrawing: optStr(body.procedure_drawing),
        plannedTime: optStr(body.planned_time),
        declarationConfirmed: bool(body.declaration_confirmed),
        warningAdvisories: Array.isArray(body.warning_advisories)
          ? body.warning_advisories.map((entry) => str(entry))
          : [],
        lines: (body.lines as unknown[]).map((entry) => line(assertRecord(entry))),
        assessors: assessors.map((entry) => {
          const member = assertRecord(entry);
          return {
            personDisplayName: str(member.person_display_name),
            isLead: bool(member.is_lead),
            roleLabel: str(member.role_label),
          };
        }),
        transitions: transitions.map((entry) => transition(assertRecord(entry))),
        resultBanner: { evaluatedUnder: evaluation },
        conductActs: Array.isArray(body.conduct_acts)
          ? body.conduct_acts.map((entry) => str(entry))
          : [],
        scheduledActivityId: optStr(body.scheduled_activity_id),
        resultsApprovedAt: optStr(body.results_approved_at),
        approvalRoute: approvalRoute(body.approval_route),
        closedWithRejections: body.closed_with_rejections === true,
      };
    },
    { signal },
  );
}

/** The server's read-back after a transition, including what it now warns about. */
export interface TransitionOutcome {
  event: string;
  fromState: string;
  toState: string;
  warningAdvisories: string[];
}

export async function transitionVerification(
  token: string,
  verificationId: string,
  event: string,
  payload: { comment?: string | null; declaration_confirmed?: boolean } = {},
): Promise<TransitionOutcome> {
  return apiJson(
    token,
    `/verifications/${verificationId}/transitions/${event}`,
    (raw) => {
      const body = assertRecord(raw);
      return {
        event: str(body.event),
        fromState: str(body.from_state),
        toState: str(body.to_state),
        warningAdvisories: Array.isArray(body.warning_advisories)
          ? body.warning_advisories.map((entry) => str(entry))
          : [],
      };
    },
    { method: "POST", body: payload },
  );
}

export interface LineOutcomePayload {
  outcome_value: string;
  comments?: string | null;
  is_positive_observation: boolean;
}

/** Record one line's outcome. A locked line is refused by the server, not by this client. */
export async function setLineOutcome(
  token: string,
  verificationId: string,
  lineId: string,
  payload: LineOutcomePayload,
): Promise<{ lineNo: number; outcomeValue: string }> {
  return apiJson(
    token,
    `/verifications/${verificationId}/lines/${lineId}/outcome`,
    (raw) => {
      const body = assertRecord(raw);
      return { lineNo: num(body.line_no), outcomeValue: str(body.outcome_value) };
    },
    { method: "PUT", body: payload },
  );
}
